import React from 'react';
import { Cell, Row } from 'react-table';
import { Box, Checkbox, HStack, Text, useToast } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { MdPersonAddAlt1, MdRemoveRedEye, MdDoNotDisturb } from 'react-icons/md';
import { CountChip } from '../recruiters/CountChip';
import type { Position } from '../types';
import colors from '../../../../../styles/colors';
import { useStoreActions, useStoreState } from '../../../../../models/hooks';

interface TableDataProps {
  children: React.ReactNode;
  cell: Cell<Position>;
}
function TableData({ children, cell }: TableDataProps) {
  const tableColor = colors.table;
  const { selectedPosition, isPositionSelected } = useStoreState((state) => {
    return state.moniterByRecruiters;
  });
  const isSelected = selectedPosition?.id === cell.row.original.id || isPositionSelected(cell.row.original.id);

  return (
    <Box
      {...cell.getCellProps()}
      px={2}
      py={1}
      display="flex"
      alignItems="center"
      sx={{
        height: '100%',
        '&:not(:last-child)': {
          borderRight: '1px solid',
          borderColor: isSelected ? tableColor.selectedBorder : tableColor.borderColor,
        },
      }}
    >
      {children}
    </Box>
  );
}

interface PositionTableRowProps {
  row: Row<Position>;
  style: React.CSSProperties;
}

function PositionTableRowComponent({ row, style }: PositionTableRowProps) {
  const { t } = useTranslation('moniterByRecruiters');
  const toast = useToast();
  const tableColor = colors.table;
  const { setSelectedPosition, setSelectAPosition } = useStoreActions((state) => state.moniterByRecruiters);
  const { selectedPosition, isBulkAssignMode, isPositionSelected } = useStoreState((state) => {
    return state.moniterByRecruiters;
  });
  const updatingRecruiterId = useStoreState((s) => s.moniterByRecruiters.statusUpdatingRecruiterId);
  const selectedRecruiters = useStoreState((state) => state.moniterByRecruiters.selectedRecruiters);
  const isSelected = selectedPosition?.id === row?.original?.id || isPositionSelected(row?.original.id);

  return (
    <Box
      {...row?.getRowProps({ style })}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isBulkAssignMode) {
          setSelectAPosition({
            positionId: row.original.id,
            businessId: row.original.business,
            onError: (message) => {
              if (!toast.isActive('max_pos_selection')) {
                toast({
                  title: message,
                  id: 'max_pos_selection',
                  isClosable: true,
                  duration: 3000,
                });
              }
            },
          });
        }
        if (updatingRecruiterId || isBulkAssignMode) return;
        setSelectedPosition(row.original);
      }}
      cursor="pointer"
      display="flex"
      alignItems="center"
      flexDirection="row"
      borderBottom={`1px solid ${tableColor.borderColor}`}
      outline={isSelected ? `1px solid ${tableColor.selectedBorder}` : '1px solid transparent'}
      outlineOffset="-1px"
      bg={isSelected ? tableColor.selectedBg : 'transparent'}
      _hover={{
        bg: isSelected ? tableColor.selectedBg : tableColor.hoverBg,
        cursor: updatingRecruiterId ? 'progress' : 'pointer',
      }}
      data-testid="table-row"
      data-selected={isSelected}
    >
      {row.cells.map((cell) => {
        const position = cell.row.original;
        switch (cell.column.id) {
          case 'checkbox':
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Checkbox
                  isChecked={isPositionSelected(position.id)}
                  onChange={() => {
                    setSelectAPosition({
                      positionId: position.id,
                      businessId: position.business,
                      onError: /* istanbul ignore next */ (message) => {
                        if (!toast.isActive('max_pos_selection')) {
                          toast({
                            title: message,
                            id: 'max_pos_selection',
                            isClosable: true,
                            duration: 3000,
                          });
                        }
                      },
                    });
                  }}
                />
              </TableData>
            );
          case 'location':
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Box>
                  <HStack>
                    <Text fontSize="xs" color="#868686" noOfLines={1} isTruncated>
                      {position.businessName}
                    </Text>
                  </HStack>
                  <Text fontSize="sm" lineHeight={1.2} noOfLines={1} isTruncated>
                    {position.customName}
                  </Text>
                </Box>
              </TableData>
            );

          case 'province':
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Text fontSize="sm">{position.state}</Text>
              </TableData>
            );
          case 'city':
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Text fontSize="sm">{position.city}</Text>
              </TableData>
            );
          case 'followed':
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Box display="flex" justifyContent="center" w="100%">
                  <CountChip
                    count={position?.assignedRecruitersCount ?? 0}
                    title={t('positions.row.stats.following')}
                    Icon={MdPersonAddAlt1}
                    data-testid="followed-count"
                  />
                </Box>
              </TableData>
            );
          case 'watched':
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Box display="flex" justifyContent="center" w="100%">
                  <CountChip
                    count={position?.watchedRecruitersCount ?? 0}
                    title={t('positions.row.stats.watching')}
                    Icon={MdRemoveRedEye}
                    data-testid="watched-count"
                  />
                </Box>
              </TableData>
            );
          case 'none': {
            const followedCount = position?.assignedRecruitersCount ?? 0;
            const watchedCount = position?.watchedRecruitersCount ?? 0;
            const selectedCount = selectedRecruiters?.length ?? 0;
            const noneCount = selectedCount - followedCount - watchedCount;
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Box display="flex" justifyContent="center" w="100%">
                  <CountChip
                    count={noneCount > 0 ? noneCount : 0}
                    title={t('positions.row.stats.none')}
                    Icon={MdDoNotDisturb}
                    data-testid="non-following-count"
                  />
                </Box>
              </TableData>
            );
          }

          default:
            return (
              <TableData cell={cell} key={cell.column.id}>
                <Text fontSize="sm" lineHeight={1.2} noOfLines={1} isTruncated>
                  {cell.render('Cell')}
                </Text>
              </TableData>
            );
        }
      })}
    </Box>
  );
}

export const PositionTableRow = React.memo(PositionTableRowComponent);
